import { mergeTemplate } from './base-template';

export const consultingTemplate = mergeTemplate({
  name: 'Consulting',
  icon: '🎯',
  settings: {
    primaryColor: '#18181b',
    accentColor: '#f59e0b',
    fontStack: 'instrument',
    stylePreset: 'swiss',
    pageAngebot: {
      heroTitle: 'Beratungsangebot',
      heroSubtitle: 'Wirksame Beratung für messbaren Fortschritt',
      packages: [
        { name: 'Workshop', price: "ab CHF 3'500/Tag", description: 'Fokussierte Arbeitssession zu einem Thema', features: 'Vorbereitung, Moderation, Methodik, Dokumentation, Follow-up', highlighted: false },
        { name: 'Projektberatung', price: 'ab CHF 250/h', description: 'Begleitung eines Veränderungsprojekts', features: 'Analyse, Konzept, Implementierungsbegleitung, Change-Management, Reporting', highlighted: true },
        { name: 'Retainer', price: "ab CHF 2'500/Mt.", description: 'Laufendes Sparring und Beratung', features: 'Monatliches Kontingent, Strategieberatung, Ad-hoc-Support, Management-Sparring', highlighted: false },
      ],
      ctaTitle: 'Veränderung beginnt mit Klarheit',
      ctaText: 'Lassen Sie uns über Ihre Herausforderungen sprechen — im vertraulichen Rahmen.',
      ctaButtonText: 'Erstgespräch vereinbaren',
    },
    pageMethoden: {
      heroTitle: 'Unsere Methodik',
      heroSubtitle: 'Evidenzbasiert, pragmatisch, wirkungsorientiert',
      introText: 'Wir verbinden bewährte Frameworks mit praxisorientierter Umsetzung — kein Berater-Deutsch, sondern konkrete Ergebnisse.',
      steps: [
        { title: 'Assessment', description: 'Ist-Analyse, Stakeholder-Mapping, Problemdefinition' },
        { title: 'Konzept', description: 'Lösungsentwicklung, Massnahmenplan, Quick Wins identifizieren' },
        { title: 'Implementierung', description: 'Hands-on Umsetzung mit Ihrem Team, nicht im Elfenbeinturm' },
        { title: 'Transfer & Verankerung', description: 'Wissenstransfer, Befähigung, Nachhaltigkeit sicherstellen' },
      ],
      tools: [
        { name: 'Business Model Canvas', description: 'Geschäftsmodell-Analyse und -Entwicklung' },
        { name: 'OKR Framework', description: 'Zieldefinition und Fortschrittsmessung' },
        { name: 'Design Thinking', description: 'Nutzerzentrierte Problemlösung' },
      ],
      philosophyTitle: 'Beratungsphilosophie',
      philosophyText: 'Gute Beratung macht sich selbst überflüssig. Wir befähigen, statt abhängig zu machen.',
    },
    pageUeberMich: {
      heroTitle: 'Über uns',
      heroSubtitle: 'Berater mit Umsetzungserfahrung',
      introText: 'Unser Team vereint Strategiekompetenz mit operativer Erfahrung — aus der Linie, nicht nur aus der Beratung.',
      timeline: [
        { year: '2012', text: 'Gründung als Boutique-Beratung' },
        { year: '2018', text: 'Fokus auf Digitalisierung und Transformation' },
        { year: '2023', text: 'Erweiterung um Interimsmanagement' },
      ],
      values: [
        { title: 'Wirkung', description: 'Ergebnisse, die man messen kann — nicht nur Slide-Decks' },
        { title: 'Partnerschaft', description: 'Auf Augenhöhe mit Ihrem Management-Team' },
        { title: 'Ehrlichkeit', description: 'Auch unbequeme Wahrheiten gehören zur guten Beratung' },
      ],
      certifications: 'CMC (Certified Management Consultant), ICF-Mitglied',
    },
  },
  components: {
    header: {
      companyName: 'Consulting Muster',
      logoText: 'CM',
      navItems: ['Leistungen', 'Ansatz', 'Team', 'Kontakt'],
      ctaText: 'Sparring buchen',
    },
    hero: {
      title: 'Strategie. Umsetzung. Wirkung.',
      subtitle:
        'Unternehmensberatung mit Fokus auf messbare Resultate.',
      primaryBtn: 'Sparring buchen',
      secondaryBtn: 'Cases',
    },
    trust: {
      items: [
        { value: 'Pragmatisch', label: 'Ansatz' },
        { value: 'Messbar', label: 'Resultate' },
        { value: 'Hands-on', label: 'Umsetzung' },
        { value: 'KMU', label: 'Fokus' },
      ],
    },
    services: {
      title: 'Expertise',
      subtitle: 'Fokussiert auf das, was zählt.',
      items: [
        {
          icon: '🎯',
          title: 'Strategie',
          text: 'Positionierung, Geschäftsmodell, Wachstum.',
        },
        {
          icon: '⚙️',
          title: 'Prozesse',
          text: 'Effizienz, Digitalisierung, Skalierung.',
        },
        {
          icon: '👥',
          title: 'Organisation',
          text: 'Struktur, Führung, Change Management.',
        },
      ],
    },
    proofTable: {
      title: 'Der Unterschied',
      subtitle: '',
      headers: ['Kriterium', 'Mit Sparring', 'Allein'],
      highlightColumn: 1,
      rows: [
        {
          criterion: 'Perspektive',
          us: 'Externer Blick, neue Impulse',
          other: 'Betriebsblindheit',
        },
        {
          criterion: 'Umsetzung',
          us: 'Strukturiert mit Milestones',
          other: 'Ad-hoc, oft verschoben',
        },
        {
          criterion: 'Verantwortung',
          us: 'Shared Accountability',
          other: 'Nur interne Treiber',
        },
      ],
    },
    process: {
      title: 'Methodik',
      subtitle: '',
      steps: [
        {
          num: '1',
          title: 'Diagnose',
          text: 'Situation verstehen, Hebel identifizieren.',
        },
        {
          num: '2',
          title: 'Konzept',
          text: 'Optionen entwickeln, priorisieren.',
        },
        {
          num: '3',
          title: 'Umsetzung',
          text: 'Begleiten, anpassen, liefern.',
        },
        {
          num: '4',
          title: 'Review',
          text: 'Wirkung messen, Learnings sichern.',
        },
      ],
    },
    team: {
      title: 'Unser Team',
      subtitle: 'Berater mit Umsetzungserfahrung.',
      members: [
        {
          name: 'Lukas Hartmann',
          role: 'Managing Partner',
          image: '',
          bio: 'Strategie, Transformation, Interimsmanagement. Ex-McKinsey und KMU-Geschäftsführer.',
        },
        {
          name: 'Carla Bianchi',
          role: 'Senior Consultant',
          image: '',
          bio: 'Prozessoptimierung, Digitalisierung, Change Management.',
        },
      ],
    },
    testimonials: {
      title: 'Kundenfeedback',
      items: [
        {
          quote: 'Klar, direkt, umsetzbar — genau das brauchten wir.',
          author: 'René Müller',
          role: 'CEO, Maschinenbau KMU',
        },
        {
          quote: 'Endlich ein Berater, der nicht nur Folien liefert, sondern auch bei der Umsetzung dabei ist.',
          author: 'Karin Studer',
          role: 'Geschäftsführerin, Dienstleistungsunternehmen',
        },
        {
          quote: 'Die Strategiearbeit hat uns als Geschäftsleitung richtig weitergebracht. Sehr pragmatisch.',
          author: 'Michael Bühlmann',
          role: 'VR-Präsident, Handelsunternehmen',
        },
        {
          quote: 'Der Workshop hat unser Team neu ausgerichtet. Hervorragende Moderation.',
          author: 'Simone Keller',
          role: 'HR-Leiterin, Technologiefirma',
        },
      ],
    },
    faq: {
      title: 'FAQ',
      subtitle: '',
      items: [
        {
          question: 'Wie lange dauert ein typisches Projekt?',
          answer:
            'Je nach Umfang 4 bis 12 Wochen. Wir definieren Scope und Milestones gemeinsam im Erstgespräch.',
        },
        {
          question: 'Für welche Unternehmensgrösse beraten Sie?',
          answer:
            'Unser Fokus liegt auf Schweizer KMU mit 10 bis 250 Mitarbeitenden. Wir kennen die Herausforderungen und Dynamiken dieser Unternehmensgrösse.',
        },
        {
          question: 'Was kostet ein Erstgespräch?',
          answer:
            'Das erste Gespräch ist unverbindlich und kostenlos. Danach erhalten Sie eine transparente Offerte mit klarem Scope.',
        },
      ],
    },
    cta: {
      title: 'Sparring buchen',
      subtitle:
        'Schildern Sie die Herausforderung – wir skizzieren einen Ansatz.',
      phone: '+41 00 000 00 00',
      email: 'info@example.ch',
    },
    footer: {
      companyName: 'Consulting Muster',
      tagline: 'Beratung für KMU.',
      address: 'Musterstrasse 1, 8000 Zürich',
      phone: '+41 00 000 00 00',
      email: 'info@example.ch',
    },
  },
});
